import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { passwordSchema, pseudoSchema } from '@cercle/shared';
import { PasswordInput } from './PasswordInput';
import { useApp } from '@/shared/app-context';
import { errorMessage } from '@/shared/http/http-client';
import { useAsync } from '@/shared/hooks/useAsync';
import { Button, ErrorText, Field, Input, Kicker, Muted, Skeleton } from '@/shared/ui';
import styles from './auth.module.scss';

/**
 * Écran 00 bis — inscription sur invitation.
 *
 * Deux informations et rien de plus : pseudo et avatar facultatif. L'email vient de
 * l'invitation, il n'est pas modifiable — c'est lui qui identifie l'invité.
 */
export function InviteScreen(): ReactNode {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { api, setMember } = useApp();

  // « J'ai un code d'invitation » : arrivée sans jeton dans l'URL, on le demande.
  if (!token) return <InviteCodeEntry />;

  return <InviteAcceptForm token={token} api={api} setMember={setMember} navigate={navigate} />;
}

function InviteCodeEntry(): ReactNode {
  const navigate = useNavigate();
  const [code, setCode] = useState('');

  return (
    <div className={styles.gate}>
      <div className={styles.gateInner}>
        <Kicker>Sur invitation uniquement</Kicker>
        <h1>Votre code d’invitation</h1>
        <form
          className={styles.form}
          onSubmit={(event) => {
            event.preventDefault();
            if (code.trim()) void navigate(`/invite/${code.trim()}`);
          }}
        >
          <Field label="Code" hint="Il figure dans le lien reçu par email.">
            <Input value={code} onChange={(event) => setCode(event.target.value)} required />
          </Field>
          <Button type="submit" block>
            Continuer
          </Button>
        </form>
      </div>
    </div>
  );
}

interface AcceptFormProps {
  token: string;
  api: ReturnType<typeof useApp>['api'];
  setMember: ReturnType<typeof useApp>['setMember'];
  navigate: ReturnType<typeof useNavigate>;
}

function InviteAcceptForm({ token, api, setMember, navigate }: AcceptFormProps): ReactNode {

  const invitation = useAsync(() => api.auth.describeInvitation(token), [token]);
  const [pseudo, setPseudo] = useState('');
  const [password, setPassword] = useState('');
  const [available, setAvailable] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Vérification « disponible » en direct, comme sur la maquette. Débouncée : sans cela,
  // chaque frappe déclencherait une requête.
  useEffect(() => {
    const parsed = pseudoSchema.safeParse(pseudo);
    if (!parsed.success) {
      setAvailable(null);
      return;
    }
    const timer = setTimeout(() => {
      api.auth
        .pseudoAvailable(parsed.data)
        .then((result) => setAvailable(result.available))
        .catch(() => setAvailable(null));
    }, 350);
    return () => clearTimeout(timer);
  }, [api, pseudo]);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    const parsed = pseudoSchema.safeParse(pseudo);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Pseudo invalide.');
      return;
    }
    const parsedPassword = passwordSchema.safeParse(password);
    if (!parsedPassword.success) {
      setError(parsedPassword.error.issues[0]?.message ?? 'Mot de passe invalide.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const member = await api.auth.acceptInvitation({
        token,
        pseudo: parsed.data,
        password: parsedPassword.data,
      });
      setMember(member);
      void navigate('/', { replace: true });
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  if (invitation.loading) {
    return (
      <div className={styles.gate}>
        <div className={styles.gateInner}>
          <Skeleton />
        </div>
      </div>
    );
  }

  if (invitation.error || !invitation.data) {
    return (
      <div className={styles.gate}>
        <div className={styles.gateInner}>
          <h1>Invitation inutilisable</h1>
          <p>{invitation.error ?? 'Cette invitation est introuvable.'}</p>
          <Muted>Demandez-en une nouvelle au fondateur du cercle.</Muted>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.gate}>
      <div className={styles.gateInner}>
        <Kicker>
          {invitation.data.invitedByPseudo
            ? `Invité par ${invitation.data.invitedByPseudo}`
            : 'Invitation au cercle'}
        </Kicker>
        <h1>Bienvenue au cercle</h1>
        <p className={styles.lede}>Deux informations et vous êtes des nôtres.</p>

        <form className={styles.form} onSubmit={(event) => void submit(event)} noValidate>
          <Field label="Pseudo" hint="Il vous identifiera dans les notes et les classements.">
            <Input
              value={pseudo}
              onChange={(event) => setPseudo(event.target.value)}
              placeholder="Camille"
              autoComplete="nickname"
              required
            />
          </Field>
          {available !== null && (
            <span
              className={[styles.availability, available ? styles.available : styles.taken].join(' ')}
            >
              {available ? 'disponible' : 'déjà pris'}
            </span>
          )}

          <Field
            label="Mot de passe"
            hint="Une phrase que vous seul retiendrez vaut mieux qu’un mot compliqué."
          >
            <PasswordInput
              autoComplete="new-password"
              value={password}
              onChange={setPassword}
              showStrengthHint
              required
            />
          </Field>

          <Field label="Email">
            <Input value={invitation.data.email} readOnly disabled />
          </Field>

          <ErrorText>{error}</ErrorText>
          <Button type="submit" block disabled={busy || available === false}>
            {busy ? 'Entrée…' : 'Rejoindre le cercle'}
          </Button>
          <Muted>Email, pseudo et mot de passe · aucune donnée de plus.</Muted>
        </form>
      </div>
    </div>
  );
}
